import log from "logbeacon";
import { cn } from "utils";
import { EdgenModalCreateAble } from "../../EdgenModal";
import { waitForAppModalEligible } from "@/src/hooks/useGlobalAppModalLimit";
import { getBridge, MessageType, waitForAppFeatures } from "@/src/lib/bridge";
import { AlphaPickPricingModalCallableParams } from "./types";
import { PaywallModalContent } from "./PaywallModalContent";

/** 用户可能在原生付费墙上停留很久（读文案、走 IAP 支付流程），默认 30s 超时远不够 */
const NATIVE_PAYWALL_TIMEOUT_MS = 60_000;

/**
 * 尝试把付费墙交给原生渲染。
 *
 * @returns `true` / `false` 表示原生已接管并给出购买结果；`null` 表示未接管，调用方继续走 Web 付费墙
 */
async function tryDelegateToNativePaywall(
  params: AlphaPickPricingModalCallableParams,
): Promise<boolean | null> {
  const { onClose, signal, ...rest } = params;

  let inApp = false;
  try {
    inApp = getBridge().isInApp();
  } catch {
    return null;
  }
  if (!inApp) {
    return null;
  }

  // 等 features 就绪再判定，避免 APP_INIT 慢于首帧时误判为老 App 而闪现 Web 付费墙
  const features = await waitForAppFeatures();
  if (features?.nativePaywall !== true) {
    return null;
  }
  // 等待期间调用方可能已离开页面（如详情页卸载）
  if (signal?.aborted) {
    return false;
  }

  let response;
  try {
    response = await getBridge().request(
      MessageType.OPEN_NATIVE_PAYWALL,
      {
        module: rest.trackingModuleName,
        symbol: rest.trackingSymbol,
        opportunityId: rest.opportunity?.id,
        expectedReturn: rest.opportunity?.expected_return,
        copyOverrides: rest.paywallCopyOverrides,
        // 影响付费墙形态的上下文：原生这版未必全部消费，但 Web 与 App 是跨分支异步发版，
        // 二次补字段的成本远高于现在多传，原生后续做动态文案/变体时无需再排一次 Web 发版。
        needCreditAmount: rest.needCreditAmount,
        credits: rest.credits,
        routerType: rest.routerType,
        hideCloseButton: rest.hideCloseButton,
        showJustLookAroundButton: rest.showJustLookAroundButton,
        heroBackgroundSrc: rest.heroBackgroundSrc,
      },
      { timeoutMs: NATIVE_PAYWALL_TIMEOUT_MS },
    );
  } catch (e) {
    // 桥超时 / 未连接 / 原生返回失败：降级回 Web 付费墙。
    // 这里绝不能 return false —— 8 个调用方都是裸 await，返回 false 会让详情页永久停在骨架屏。
    log.warn("[AlphaPickPricing] native paywall delegate failed", e);
    return null;
  }

  // request 的 resolve 值是 ResponseMessage 信封而非 payload；原生协议为 data = { purchased }，
  // 同时兼容旧实现直接返回 boolean 的形状（与 bridge 其他解包方法一致）。
  const data = response?.data;
  const purchased = data?.purchased === true || data === true;

  // 未购买（用户主动关闭 / 取消）等价于 Web 侧 handleClose：必须执行调用方的 dismiss 副作用，
  // 否则详情页这类依赖 onClose 退回列表页的调用方会停在锁定态骨架屏上。
  if (!purchased) {
    onClose?.();
  }
  return purchased;
}

export const AlphaPickPricingModalCallable = async (
  params: AlphaPickPricingModalCallableParams,
) => {
  const { isPC = false, onClose, onNavigateAway, signal, ...rest } = params;

  // App 多 WebView：不可见 / freeze 的容器里弹出的付费墙会残留到下次复用该 WebView 时才被看到。
  // 统一在此收口，调用方无需各自处理。
  if (!(await waitForAppModalEligible(signal))) {
    return false;
  }

  // 原生付费墙委托必须在可见性 gate 之后：离屏 / 冻结的 WebView 触发原生付费墙，
  // 会让用户在当前所处的另一个原生页面上莫名冒出付费墙，比 Web modal 残留更糟。
  const nativeResult = await tryDelegateToNativePaywall(params);
  if (nativeResult !== null) {
    return nativeResult;
  }

  const handleClose = () => {
    onClose?.();
    EdgenModalCreateAble.end(false);
  };
  // 导航类动作（「View all plans」/ 跳定价页）只关弹窗，不执行调用方 onClose 的副作用，
  // 避免同 tick 双导航（调用方 onClose 常带 router.push 回列表页）。
  const handleNavigateAway = () => {
    onNavigateAway?.();
    EdgenModalCreateAble.end(false);
  };
  const result = await EdgenModalCreateAble.call({
    children: (
      <PaywallModalContent
        isPc={isPC}
        {...rest}
        onClose={handleClose}
        onNavigateAway={handleNavigateAway}
      />
    ),
    zIndex: rest.zIndex,
    containerClassName: cn(
      "bg-background_solid_neutral_secondary",
      isPC ? "w-[480px] h-[712px]" : "w-full h-full",
    ),
    contentStyle: {
      borderRadius: isPC ? 16 : 0,
      width: isPC ? 480 : "100%",
      height: isPC ? 712 : "100%",
      padding: 0,
      overflow: "hidden",
    },
    containerStyle: {
      maxHeight: "100%",
    },
    destroyOnClose: true,
    onCancel: handleClose,
    showClose: false,
  });

  return result;
};
